import { CalendarBaseProps } from 'primereact/calendar';
import { PrimeReactStylesFunctionType } from '../types';
import { classNames } from 'primereact/utils';

export const CalendarTailwindThemes = {
  calendar: {
    root: ({ props, state }: PrimeReactStylesFunctionType) => ({
      className: classNames('inline-flex max-w-full relative', {
        'opacity-60 select-none pointer-events-none cursor-default': props.disabled,
        '[&>input[data-pc-section="root"]]:border-fletchers-green-500': state.overlayVisible,
        ['[&:has(button[data-pc-section="root"]:hover)>input[data-pc-section="root"]]:border-fletchers-green-500 ' +
        '[&:has(input[data-pc-section="root"]:hover)>button[data-pc-section="root"]]:border-fletchers-green-500 ' +
        '[&:has(input[data-pc-section="root"]:hover)>button[data-pc-section="root"]>svg>path]:fill-fletchers-green-500']:
          props.showIcon,
        ['[&>button[data-pc-section="root"]]:border-fletchers-green-500 ' +
        '[&>button[data-pc-section="root"]>svg>path]:fill-fletchers-green-500']:
          props.showIcon && (state.focused || state.overlayVisible),
      }),
    }),
    input: ({ props }: PrimeReactStylesFunctionType) => ({
      root: {
        className: classNames(
          'pl-2 py-1 w-full transition-colors duration-200 appearance-none',
          'font-sans text-sm text-black border border-fletchers-gray-100 bg-white',
          'hover:border-fletchers-green-500',
          {
            'pr-2 rounded-sm': !props.showIcon,
            'pr-0 border-r-0 rounded-l-sm rounded-r-none': props.showIcon,
          },
        ),
      },
    }),
    dropdownButton: {
      root: ({ props }: PrimeReactStylesFunctionType) => ({
        className: classNames({
          ['shrink-0 transition-colors duration-200 appearance-none outline-none ' +
          'border-l-0 rounded-l-none rounded-r-sm border-fletchers-gray-100 bg-transparent ' +
          'hover:border-fletchers-green-500 hover:bg-transparent hover:[&>svg>path]:fill-fletchers-green-500 ' +
          'focus-visible:bg-fletchers-green-50 ' +
          '[&>svg>path]:transition-colors [&>svg>path]:duration-200']: props.icon,
        }),
      }),
    },
    panel: ({ props }: PrimeReactStylesFunctionType) => ({
      className: classNames(
        'min-w-min border rounded-lg border-fletchers-gray-100 bg-white',
        {
          'shadow-sm absolute mt-1': !props.inline,
          'inline-block overflow-x-auto p-2': props.inline,
        },
        (props as CalendarBaseProps).panelClassName,
      ),
    }),
    header: {
      className: classNames('flex items-center justify-between', 'p-3.5 rounded-t-lg text-zinc-600 bg-white'),
    },
    previousButton: {
      className: classNames(
        'flex items-center justify-center cursor-pointer overflow-hidden relative',
        'outline-transparent',
        'size-6 transition-colors duration-200 ease-in-out border-0 rounded-full bg-transparent',
        'hover:bg-fletchers-green-50',
        'focus-visible:outline-2 focus-visible:outline-fletchers-green-500',
      ),
    },
    title: { className: 'mx-auto' },
    monthTitle: {
      className: classNames(
        'mx-2 transition duration-200 cursor-pointer text-sm font-normal text-zinc-600',
        'outline-transparent rounded-xs',
        'hover:text-black',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fletchers-green-500',
      ),
    },
    yearTitle: {
      className: classNames(
        'mr-2 transition duration-200 cursor-pointer text-sm font-normal text-zinc-600',
        'outline-transparent rounded-xs',
        'hover:text-black',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fletchers-green-500',
      ),
    },
    nextButton: {
      className: classNames(
        'flex items-center justify-center cursor-pointer overflow-hidden relative',
        'outline-transparent',
        'size-6 transition-colors duration-200 ease-in-out border-0 rounded-full bg-transparent',
        'hover:bg-fletchers-green-50',
        'focus-visible:outline-2 focus-visible:outline-fletchers-green-500',
      ),
    },
    table: { className: 'border-collapse w-full' },
    tableHeaderCell: {
      className: 'w-8 h-10 first-of-type:rounded-l-sm last-of-type:rounded-r-sm bg-fletchers-gray-50',
    },
    tableBody: {
      className: 'before:block before:h-1 after:block after:h-1',
    },
    tableBodyRowProps: {
      className: classNames(
        '[&:has(td:last-child>[aria-selected="true"])+tr>td:first-child>[aria-selected="true"]]:rounded-l-none',
        '[&:has(+tr>td:first-child>[aria-selected="true"])>td:last-child>[aria-selected="true"]]:rounded-r-none',
      ),
    },
    weekday: {
      className: 'text-sm font-normal text-zinc-700',
    },
    day: ({ context }: PrimeReactStylesFunctionType) => ({
      className: classNames({
        '[&>[data-pc-section="daylabel"]]:text-fletchers-green-500': context.date?.today && context.date?.selectable,
      }),
    }),
    dayLabel: ({ context }: PrimeReactStylesFunctionType) => ({
      className: classNames(
        'w-[40px] h-[31px] transition-shadow duration-200 border-transparent border',
        'flex items-center justify-center mx-auto overflow-hidden relative text-xs font-normal',
        'outline-transparent',
        'focus-visible:outline-2 focus-visible:outline-fletchers-green-500 focus-visible:z-1',
        {
          // Below 4.5:1 on purpose — WCAG 1.4.3 exempts inactive components, and darkening
          // these only makes unselectable dates look selectable.
          'cursor-default text-fletchers-gray-150': context.disabled,
          'cursor-pointer text-zinc-700': !context.disabled,
        },
        {
          'bg-transparent hover:bg-fletchers-green-50': !context.selected && !context.disabled,
          ['bg-theme-highlight-green hover:bg-theme-highlight-green ' +
          'rounded-sm ' +
          '[[data-pc-section="day"]:has(&)+[data-pc-section="day"]>[aria-selected="true"]]:rounded-l-none ' +
          'font-bold [[data-pc-section="day"]:has(+[data-pc-section="day"]>&)>[aria-selected="true"]]:rounded-r-none']:
            context.selected && !context.disabled,
        },
      ),
    }),
    monthPicker: { className: 'my-2' },
    month: ({ context }: PrimeReactStylesFunctionType) => ({
      className: classNames(
        'w-1/3 inline-flex items-center justify-center cursor-pointer overflow-hidden relative',
        'p-2 transition-shadow duration-200 rounded-sm text-sm font-normal',
        'outline-transparent',
        'focus-visible:outline-2 focus-visible:outline-fletchers-green-500 focus-visible:z-1',
        {
          'text-zinc-700 bg-transparent hover:bg-fletchers-green-50': !context.selected && !context.disabled,
          'text-zinc-700 bg-fletchers-green-100 hover:bg-fletchers-green-100': context.selected && !context.disabled,
        },
      ),
    }),
    yearPicker: {
      className: classNames('my-2'),
    },
    year: ({ context }: PrimeReactStylesFunctionType) => ({
      className: classNames(
        'w-1/2 inline-flex items-center justify-center cursor-pointer overflow-hidden relative',
        'p-2 transition-shadow duration-200 rounded-sm text-sm font-normal',
        'outline-transparent',
        'focus-visible:outline-2 focus-visible:outline-fletchers-green-500 focus-visible:z-1',
        {
          'text-zinc-700 bg-transparent hover:bg-fletchers-green-50': !context.selected && !context.disabled,
          'text-zinc-700 bg-fletchers-green-100 hover:bg-fletchers-green-100': context.selected && !context.disabled,
        },
      ),
    }),
    timePicker: {
      className: classNames(
        'flex justify-center items-center',
        'border-t-1 border-solid border-fletchers-gray-100 p-2',
      ),
    },
    separatorContainer: { className: 'flex items-center flex-col px-2' },
    separator: { className: 'text-xl' },
    hourPicker: { className: 'flex items-center flex-col px-2' },
    minutePicker: { className: 'flex items-center flex-col px-2' },
    ampmPicker: { className: 'flex items-center flex-col px-2' },
    incrementButton: {
      className: classNames(
        'flex items-center justify-center cursor-pointer overflow-hidden relative',
        'w-8 h-8 text-gray-600 border-0 bg-transparent rounded-full transition-colors duration-200 ease-in-out',
        'hover:text-gray-700 hover:border-transparent hover:bg-gray-200',
      ),
    },
    decrementButton: {
      className: classNames(
        'flex items-center justify-center cursor-pointer overflow-hidden relative',
        'w-8 h-8 text-gray-600 border-0 bg-transparent rounded-full transition-colors duration-200 ease-in-out',
        'hover:text-gray-700 hover:border-transparent hover:bg-gray-200',
      ),
    },
    groupContainer: { className: 'flex' },
    group: {
      className: classNames('flex-1', 'border-l border-fletchers-gray-100', 'first:border-l-0'),
    },
    container: { className: 'px-1' },
    transition: {
      timeout: 150,
      classNames: {
        enter: 'opacity-0 scale-75',
        enterActive: 'opacity-100 !scale-100 transition-transform transition-opacity duration-150 ease-in',
        exit: 'opacity-100',
        exitActive: '!opacity-0 transition-opacity duration-150 ease-linear',
      },
    },
  },
};
